import { useMemo } from "react";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  EntityReviewItem,
  useGetGroupReviewDetailsQuery,
} from "v2/store/onboardingEntityApi";
import { countryName } from "v2/utils/countryName";
import { joinLegalName } from "v2/utils/joinLegalName";
import {
  CompanyOverview,
  ENTITY_STATUS,
  EntityStatus,
  OverviewEntity,
  OverviewTreeNode,
} from "./brandOverview.types";
import { buildOverviewTree } from "./buildOverviewTree";
import {
  MISSING_LABEL,
  RELATION_PARENT,
  RELATION_SUBSIDIARY,
} from "./constants";
import { Entity } from "types/Models/entity";

export type BrandOverviewData = {
  company: CompanyOverview | null;
  entities: OverviewEntity[];
  tree: OverviewTreeNode[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => void;
};

const statusOf = (
  item: EntityReviewItem,
  currentEntityStatus: Entity["entity_status"] | undefined
): EntityStatus => {
  if (item.verificationPending) {
    return ENTITY_STATUS.PRE_INCORPORATED;
  } else {
    return currentEntityStatus || "ACTIVE";
  }
};

const missingDetailsOf = (item: EntityReviewItem): string[] => {
  const missing: string[] = [];

  if (!item.ein) missing.push(MISSING_LABEL.EIN);
  if (!item.dateOfIncorporation)
    missing.push(MISSING_LABEL.DATE_OF_INCORPORATION);
  if (item.states.length === 0) missing.push(MISSING_LABEL.STATES);

  return missing;
};

// maps each entity to its parent id using the related-entity relation strings
const buildParentMap = (items: EntityReviewItem[]): Map<string, string> => {
  const parentMap = new Map<string, string>();

  items.forEach((item) => {
    item.relatedEntities.forEach((related) => {
      const relation = related.relation.toLowerCase();

      if (relation.includes(RELATION_SUBSIDIARY)) {
        parentMap.set(related.id, item.entityId);
      }

      if (relation.includes(RELATION_PARENT)) {
        parentMap.set(item.entityId, related.id);
      }
    });
  });

  return parentMap;
};

const toOverviewEntity = (
  item: EntityReviewItem,
  parentMap: Map<string, string>,
  currentEntityStatus: Entity["entity_status"] | undefined
): OverviewEntity => {
  const homeState = item.states.find((state) => state.isHome) ?? item.states[0];

  return {
    id: item.entityId,
    name:
      item.registeredName || joinLegalName(item.entityName, item.entitySuffix),
    entityTypeLabel: item.entityTypeLabel,
    countryCode: item.countryCode,
    countryName: countryName(item.countryCode),
    homeStateName: homeState?.name ?? "",
    status: statusOf(item, currentEntityStatus),
    dateOfIncorporation: item.dateOfIncorporation,
    ein: item.ein,
    states: item.states,
    missingDetails: missingDetailsOf(item),
    parentId: parentMap.get(item.entityId) ?? null,
  };
};

const toCompanyOverview = (
  groupName: string,
  entities: OverviewEntity[],
  tree: OverviewTreeNode[],
  website: string
): CompanyOverview => {
  const operatingCountries = Array.from(
    new Map(
      entities
        .filter((entity) => entity.countryCode)
        .map((entity) => [
          entity.countryCode,
          { code: entity.countryCode, name: entity.countryName },
        ])
    ).values()
  );

  const parentEntity = tree[0]?.entity;
  const hasSiblings = tree.length > 1;

  return {
    name: groupName,
    website,
    countryName: parentEntity?.countryName ?? "",
    hasSiblings,
    operatingCountries,
  };
};

export const useBrandOverviewData = (): BrandOverviewData => {
  const {
    uuid: groupId,
    name,
    domain,
    entities: groupEntities,
  } = useCurrentGroupContext();

  const { data, isLoading, isError, isSuccess, refetch } =
    useGetGroupReviewDetailsQuery({ groupId }, { skip: !groupId });

  const groupName = name;
  const website = domain ?? "";

  const { entities, tree, company } = useMemo(() => {
    const entitiesStatus = new Map<string, Entity["entity_status"]>();
    groupEntities.forEach((item) => {
      entitiesStatus.set(item.uuid, item.entity_status);
    });

    const items = data ?? [];
    const parentMap = buildParentMap(items);
    const overviewEntities = items.map((item) => {
      const currentEntityStatus = entitiesStatus.get(item.entityId);
      return toOverviewEntity(item, parentMap, currentEntityStatus);
    });
    const overviewTree = buildOverviewTree(overviewEntities);

    return {
      entities: overviewEntities,
      tree: overviewTree,
      company: overviewEntities.length
        ? toCompanyOverview(groupName, overviewEntities, overviewTree, website)
        : null,
    };
  }, [data, groupName, website, groupEntities]);

  return { company, entities, tree, isLoading, isError, isSuccess, refetch };
};
